import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  OrganizationStatuses,
  OrderAcceptanceModes,
  organizationZ,
} from '@libs/schemas/src/organization';
import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { trpc } from 'src/trpc';
import { NumericFormat } from 'react-number-format';
import { ErrorMessage } from '@hookform/error-message';
import { z } from 'zod';
import { keepPreviousData } from '@tanstack/react-query';
import { Loader } from 'src/components/Loader';
import { useToast } from 'src/components/useToast';
import { MoneyFromCents, MoneyFromDollars, safeMoneyFromCents } from '@libs/services/money';
import { Check, Lock, Shield, Unlock } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

// Extend the organization schema to include optional fields needed for the form
const formOrganizationZ = organizationZ.extend({
  _id: z.string().optional(),
});

type OrganizationZ = z.infer<typeof formOrganizationZ>;
type OrderAcceptanceMode = (typeof OrderAcceptanceModes)[keyof typeof OrderAcceptanceModes];

interface OrderAcceptancePolicy {
  id: OrderAcceptanceMode;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
  bgTint: string;
}

const orderAcceptancePolicies: OrderAcceptancePolicy[] = [
  {
    id: OrderAcceptanceModes.OPEN,
    label: 'Open',
    description: 'Customers and admins can place orders',
    icon: Unlock,
    color: '#047857',
    bgTint: '#ECFDF5',
  },
  {
    id: OrderAcceptanceModes.ADMIN_ONLY,
    label: 'Admin Only',
    description: 'Only admins can place orders',
    icon: Shield,
    color: '#A16207',
    bgTint: '#FEFCE8',
  },
  {
    id: OrderAcceptanceModes.CLOSED,
    label: 'Closed',
    description: 'No new orders can be created',
    icon: Lock,
    color: '#DC2626',
    bgTint: '#FEF2F2',
  },
];

type FormState = {
  isValid: boolean;
  isDirty: boolean;
};

export interface OrganizationFormRef {
  save: () => Promise<void>;
}

interface OrganizationFormProps extends PropsWithChildren {
  initialData?: OrganizationZ;
  onSuccess?: () => void;
  onStateChange?: (state: FormState) => void;
}

export const OrganizationForm = forwardRef<OrganizationFormRef, OrganizationFormProps>(
  ({ initialData, onSuccess, onStateChange, children }, ref) => {
    const utils = trpc.useUtils();
    const { successToast, errorToast } = useToast();
    const [hoveredPolicy, setHoveredPolicy] = useState<OrderAcceptanceMode | null>(null);

    // Fetch all add-ons for same-day delivery dropdown (up to 1000).
    // Pass orgId explicitly so the query is scoped to the org being edited,
    // not the global org switcher state.
    const { data: addOnsData } = trpc.admin.addOn.getAll.useQuery(
      {
        offset: 0,
        limit: 1000,
        ...(initialData?._id ? { orgId: initialData._id } : {}),
      },
      {
        enabled: !!initialData?._id,
        staleTime: 30000,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
      },
    );

    // Filter non-archived add-ons for the dropdown
    const availableAddOns = addOnsData?.addOns?.filter((a) => !a.archived) ?? [];

    const { mutate: createOrganization, isPending: isCreating } =
      trpc.admin.organization.createOrganization.useMutation({
        onSuccess: () => {
          successToast({
            title: 'Organization Created',
            description: 'The organization has been successfully created.',
          });
          onSuccess?.();
          utils.admin.organization.getAllOrganizations?.invalidate();
        },
        onError: (error) => {
          errorToast({
            title: 'Error Creating Organization',
            description: error.message || 'An error occurred while creating the organization.',
          });
        },
      });

    const { mutate: updateOrganization, isPending: isUpdating } =
      trpc.admin.organization.updateOrganization.useMutation({
        onSuccess: (result) => {
          successToast({
            title: 'Organization Updated',
            description: 'The organization has been successfully updated.',
          });
          onSuccess?.();
          utils.admin.organization.getAllOrganizations?.invalidate();
          if (result?._id) {
            utils.admin.organization.getOrganization.invalidate(result._id.toString());
          }
        },
        onError: (error) => {
          errorToast({
            title: 'Error Updating Organization',
            description: error.message || 'An error occurred while updating the organization.',
          });
        },
      });

    // Transform initialData for the form

    const defaultValues: Partial<OrganizationZ> = initialData
      ? {
          ...initialData,
          pricePerTonInCents: initialData.pricePerTonInCents ?? 0,
          minimumOrderWeightInTons: initialData.minimumOrderWeightInTons ?? 0,
          collectTaxes: initialData.collectTaxes ?? true,
          minimumDistanceThresholdInMiles: initialData.minimumDistanceThresholdInMiles ?? 0,
          distanceIncrementInMiles: initialData.distanceIncrementInMiles ?? 0,
          surchargePerIncrementInCents: initialData.surchargePerIncrementInCents / 100 || 0,
          minimumSurchargeInCents: initialData.minimumSurchargeInCents / 100 || 0,
          defaultRentalDays: initialData.defaultRentalDays ?? 1,
          sameDayPickupCutoffHour: initialData.sameDayPickupCutoffHour ?? 12,
          reminderCutoffHour: initialData.reminderCutoffHour ?? 12,
          hotjarSurveyEventName: initialData.hotjarSurveyEventName ?? '',
          sameDayAddOnId: initialData.sameDayAddOnId ?? '',
          orderAcceptanceMode: initialData.orderAcceptanceMode ?? OrderAcceptanceModes.OPEN,
        }
      : {
          status: OrganizationStatuses.ACTIVE,
          pricePerTonInCents: 0,
          minimumOrderWeightInTons: 0,
          collectTaxes: true,
          minimumDistanceThresholdInMiles: 0,
          distanceIncrementInMiles: 0,
          surchargePerIncrementInCents: 0,
          minimumSurchargeInCents: 0,
          defaultRentalDays: 1,
          sameDayPickupCutoffHour: 12,
          reminderCutoffHour: 12,
          hotjarSurveyEventName: '',
          sameDayAddOnId: '',
          orderAcceptanceMode: OrderAcceptanceModes.OPEN,
        };

    const form = useForm<OrganizationZ>({
      resolver: zodResolver(formOrganizationZ),
      defaultValues,
    });

    const { isValid, isDirty, errors } = useFormState({ control: form.control });

    // Reset form when initialData changes
    useEffect(() => {
      if (initialData) {
        const resetData = {
          ...initialData,
          pricePerTonInCents: initialData.pricePerTonInCents ?? 0,
          minimumOrderWeightInTons: initialData.minimumOrderWeightInTons ?? 0,
          collectTaxes: initialData.collectTaxes ?? true,
          minimumDistanceThresholdInMiles: initialData.minimumDistanceThresholdInMiles ?? 0,
          distanceIncrementInMiles: initialData.distanceIncrementInMiles ?? 0,
          surchargePerIncrementInCents: initialData.surchargePerIncrementInCents / 100 || 0,
          minimumSurchargeInCents: initialData.minimumSurchargeInCents / 100 || 0,
          defaultRentalDays: initialData.defaultRentalDays ?? 1,
          sameDayPickupCutoffHour: initialData.sameDayPickupCutoffHour ?? 12,
          reminderCutoffHour: initialData.reminderCutoffHour ?? 12,
          hotjarSurveyEventName: initialData.hotjarSurveyEventName ?? '',
          sameDayAddOnId: initialData.sameDayAddOnId ?? '',
          orderAcceptanceMode: initialData.orderAcceptanceMode ?? OrderAcceptanceModes.OPEN,
        };

        form.reset(resetData);
      }
    }, [initialData?._id]);

    useEffect(() => {
      onStateChange?.({ isValid, isDirty });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid, isDirty]);

    const onSubmit = (values: OrganizationZ) => {
      const submitValues = {
        ...values,
        surchargePerIncrementInCents: values.surchargePerIncrementInCents * 100 || 0,
        minimumSurchargeInCents: values.minimumSurchargeInCents * 100 || 0,
        defaultRentalDays: values.defaultRentalDays ?? 1,
        sameDayPickupCutoffHour: values.sameDayPickupCutoffHour ?? 12,
      };

      if (initialData?._id) {
        updateOrganization({
          _id: initialData._id,
          ...submitValues,
        });
      } else {
        createOrganization({
          ...submitValues,
        });
      }
    };

    // Expose save function to parent component
    useImperativeHandle(ref, () => ({
      save: async () => {
        const isValid = await form.trigger();
        if (isValid) {
          const values = form.getValues();
          // Reset form state immediately after validation passes to prevent unsaved changes dialog
          form.reset(values);
          onSubmit(values);
        }
      },
    }));

    if (isCreating || isUpdating) {
      return (
        <Loader title={initialData?._id ? 'Updating organization' : 'Creating organization'} />
      );
    }

    return (
      <div className="space-y-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8" id="organization-form">
            {/* Organization Details Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Details</h3>
                <p className="text-sm text-muted">Basic organization information</p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Organization Name
                      </FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter organization name" />
                      </FormControl>
                      <ErrorMessage errors={errors.name} name="name" />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="pricePerTonInCents"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Price Per Ton
                        </FormLabel>
                        <FormControl>
                          <NumericFormat
                            value={field.value ? MoneyFromCents(field.value).toFormat() : ''}
                            onValueChange={(values) => {
                              const numValue = parseFloat(values.value);
                              const cents = isNaN(numValue)
                                ? null
                                : MoneyFromDollars(numValue).getAmount();
                              field.onChange(cents);
                            }}
                            thousandSeparator={true}
                            prefix={'$'}
                            customInput={Input}
                            placeholder="0.00"
                            valueIsNumericString={true}
                            decimalScale={2}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <ErrorMessage
                          errors={errors.pricePerTonInCents}
                          name="pricePerTonInCents"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="minimumOrderWeightInTons"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Minimum Order Tonnage
                        </FormLabel>
                        <FormControl>
                          <NumericFormat
                            value={field.value || ''}
                            onValueChange={(value) => {
                              const numValue = parseFloat(value.value);
                              field.onChange(isNaN(numValue) ? undefined : numValue);
                            }}
                            thousandSeparator={true}
                            customInput={Input}
                            placeholder="Enter minimum order tonnage"
                            valueIsNumericString={true}
                            decimalScale={1}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <ErrorMessage
                          errors={errors.minimumOrderWeightInTons}
                          name="minimumOrderWeightInTons"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="defaultRentalDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Default Service Days{' '}
                          <span className="text-xs text-muted">(For pricing page estimation)</span>
                        </FormLabel>
                        <FormControl>
                          <NumericFormat
                            value={field.value || ''}
                            onValueChange={(value) => {
                              const numValue = parseFloat(value.value);
                              field.onChange(isNaN(numValue) ? undefined : numValue);
                            }}
                            thousandSeparator={true}
                            customInput={Input}
                            placeholder="Enter default service days"
                            valueIsNumericString={true}
                            decimalScale={0}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <ErrorMessage errors={errors.defaultRentalDays} name="defaultRentalDays" />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">Status</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={(value) =>
                              field.onChange(
                                value as (typeof OrganizationStatuses)[keyof typeof OrganizationStatuses],
                              )
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(OrganizationStatuses).map((status) => (
                                <SelectItem key={status} value={status}>
                                  {status
                                    .toLowerCase()
                                    .replace('-', ' ')
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <ErrorMessage errors={errors.status} name="status" />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6"></div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">Timezone</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select timezone" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="America/New_York">
                                Eastern Time (ET) - New York
                              </SelectItem>
                              <SelectItem value="America/Chicago">
                                Central Time (CT) - Chicago
                              </SelectItem>
                              <SelectItem value="America/Denver">
                                Mountain Time (MT) - Denver
                              </SelectItem>
                              <SelectItem value="America/Phoenix">
                                Mountain Time (MT) - Phoenix (No DST)
                              </SelectItem>
                              <SelectItem value="America/Los_Angeles">
                                Pacific Time (PT) - Los Angeles
                              </SelectItem>
                              <SelectItem value="America/Anchorage">
                                Alaska Time (AKT) - Anchorage
                              </SelectItem>
                              <SelectItem value="Pacific/Honolulu">
                                Hawaii Time (HST) - Honolulu
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <ErrorMessage errors={errors.timezone} name="timezone" />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sameDayPickupCutoffHour"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Same-Day Order Cutoff Hour
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="23"
                            placeholder="12"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const value =
                                e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                              field.onChange(isNaN(value as number) ? undefined : value);
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-muted">
                          Hour (0-23) for same-day order cutoff. Orders placed after this hour will
                          be scheduled for the next day. Default: 12 (noon).
                        </p>
                        <ErrorMessage
                          errors={errors.sameDayPickupCutoffHour}
                          name="sameDayPickupCutoffHour"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="reminderCutoffHour"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Pickup Reminder Cutoff Hour (Organization Timezone)
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="23"
                            placeholder="12"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const value =
                                e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                              field.onChange(isNaN(value as number) ? undefined : value);
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-muted">
                          Hour (0-23) in organization timezone for scheduling pickup reminder tasks.
                          Reminders are sent the day before pickup at this hour. Default: 12 (noon).
                        </p>
                        <ErrorMessage
                          errors={errors.reminderCutoffHour}
                          name="reminderCutoffHour"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Email Configuration Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Email Configuration</h3>
                <p className="text-sm text-muted">Configure email settings for this organization</p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="fromEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">From Email</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter from email address" type="email" />
                      </FormControl>
                      <ErrorMessage errors={errors.fromEmail} name="fromEmail" />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="toEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">To Email</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter to email address" type="email" />
                      </FormControl>
                      <ErrorMessage errors={errors.toEmail} name="toEmail" />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="adminBccEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Admin BCC Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter admin BCC email address"
                          type="email"
                          value={field.value || ''}
                        />
                      </FormControl>
                      <ErrorMessage errors={errors.adminBccEmail} name="adminBccEmail" />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="googleReviewUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Google Review URL
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter Google review URL"
                          type="url"
                          value={field.value || ''}
                        />
                      </FormControl>
                      <ErrorMessage errors={errors.googleReviewUrl} name="googleReviewUrl" />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Payment & Billing Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Payment & Billing</h3>
                <p className="text-sm text-muted">
                  Configure payment and billing settings for this organization
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="stripeConnectAccountId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Stripe Connect Account ID
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter Stripe Connect Account ID"
                          value={field.value || ''}
                        />
                      </FormControl>
                      <ErrorMessage
                        errors={errors.stripeConnectAccountId}
                        name="stripeConnectAccountId"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="collectTaxes"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium text-muted">
                          Collect Taxes
                        </FormLabel>
                        <div className="text-xs text-muted">
                          Enable tax collection for this organization
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="data-[state=checked]:bg-primary"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Distance-Based Pricing Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Distance-Based Pricing</h3>
                <p className="text-sm text-muted text-wrap">
                  Configure distance-based surcharges for deliveries. These settings determine
                  additional charges based on delivery distance.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="minimumDistanceThresholdInMiles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Minimum Distance Threshold (miles)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="0"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="distanceIncrementInMiles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Distance Increment (miles)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          // min="0.1"
                          placeholder="0"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="surchargePerIncrementInCents"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Surcharge per Increment
                        </FormLabel>
                        <FormControl>
                          <NumericFormat
                            value={field.value}
                            onValueChange={(values) => {
                              const numValue = values.floatValue!;
                              field.onChange(isNaN(numValue) ? null : numValue);
                            }}
                            thousandSeparator={true}
                            prefix={'$'}
                            customInput={Input}
                            placeholder="0.00"
                            valueIsNumericString={true}
                            decimalScale={2}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <ErrorMessage
                          errors={errors.surchargePerIncrementInCents}
                          name="surchargePerIncrementInCents"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="minimumSurchargeInCents"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Minimum Surcharge
                        </FormLabel>
                        <FormControl>
                          <NumericFormat
                            value={field.value}
                            onValueChange={(values) => {
                              const numValue = values.floatValue!;
                              field.onChange(isNaN(numValue) ? 0 : numValue);
                            }}
                            thousandSeparator={true}
                            prefix={'$'}
                            customInput={Input}
                            placeholder="0.00"
                            valueIsNumericString={true}
                            decimalScale={2}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Same-Day Delivery Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Same-Day Delivery</h3>
                <p className="text-sm text-muted">
                  Configure automatic fees for same-day delivery orders
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="sameDayAddOnId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Same-Day Delivery Fee Add-On
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value || 'none'}
                          onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select an add-on (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {availableAddOns.map((addOn) => (
                              <SelectItem
                                key={addOn._id?.toString()}
                                value={addOn._id?.toString() || ''}
                              >
                                {addOn.title} (
                                {safeMoneyFromCents(addOn.priceInCents || 0).toFormat()})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <p className="text-xs text-muted">
                        Select an add-on to automatically charge when customers choose same-day
                        delivery. The fee will be auto-selected and locked during checkout.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Integrations Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Integrations</h3>
                <p className="text-sm text-muted">Configure third-party integrations</p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="hotjarSurveyEventName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Hotjar Survey Event Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., order-confirmation-survey"
                          value={field.value || ''}
                        />
                      </FormControl>
                      <p className="text-xs text-muted">
                        Event name to trigger organization-specific Hotjar survey on order
                        confirmation
                      </p>
                      <ErrorMessage
                        errors={errors.hotjarSurveyEventName}
                        name="hotjarSurveyEventName"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Order Acceptance Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Order Acceptance</h3>
                <p className="text-sm text-muted">
                  Choose who can create new orders in checkout for this organization
                </p>
              </div>

              <div>
                <FormField
                  control={form.control}
                  name="orderAcceptanceMode"
                  render={({ field }) => (
                    <FormItem>
                      {(() => {
                        const selectedMode = (field.value ||
                          OrderAcceptanceModes.OPEN) as OrderAcceptanceMode;
                        const selectedIndex = orderAcceptancePolicies.findIndex(
                          (policy) => policy.id === selectedMode,
                        );
                        const currentPolicy =
                          orderAcceptancePolicies[selectedIndex] || orderAcceptancePolicies[0];

                        return (
                          <FormControl>
                            <div className="space-y-4">
                              <div className="px-1">
                                <FormLabel className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">
                                  Checkout Access Policy
                                </FormLabel>
                              </div>

                              <div className="rounded-xl border border-[#E2E8F0] bg-white p-1.5 shadow-sm">
                                <div className="relative grid grid-cols-1 md:grid-cols-3">
                                  <div
                                    className="pointer-events-none absolute left-0 top-0 hidden h-full rounded-lg transition-all duration-300 ease-out md:block"
                                    style={{
                                      width: 'calc(100% / 3)',
                                      transform: `translateX(${Math.max(selectedIndex, 0) * 100}%)`,
                                      backgroundColor: currentPolicy.bgTint,
                                      border: `1.5px solid ${currentPolicy.color}`,
                                    }}
                                  />

                                  {orderAcceptancePolicies.map((policy) => {
                                    const PolicyIcon = policy.icon;
                                    const isSelected = policy.id === selectedMode;
                                    const isHovered = hoveredPolicy === policy.id && !isSelected;

                                    return (
                                      <button
                                        key={policy.id}
                                        type="button"
                                        onClick={() => field.onChange(policy.id)}
                                        onMouseEnter={() => setHoveredPolicy(policy.id)}
                                        onMouseLeave={() => setHoveredPolicy(null)}
                                        aria-pressed={isSelected}
                                        className="relative z-10 flex flex-col items-start gap-2 rounded-lg px-4 py-3.5 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA5B24]/40 md:min-h-[132px]"
                                        style={{
                                          backgroundColor: isSelected
                                            ? policy.bgTint
                                            : 'transparent',
                                          border:
                                            isSelected && selectedIndex < 0
                                              ? `1.5px solid ${policy.color}`
                                              : '1.5px solid transparent',
                                        }}
                                      >
                                        <div className="flex w-full items-center justify-between">
                                          <PolicyIcon
                                            className="h-5 w-5 transition-colors duration-200"
                                            strokeWidth={2}
                                            style={{
                                              color: isSelected
                                                ? policy.color
                                                : isHovered
                                                  ? policy.color
                                                  : '#64748B',
                                            }}
                                          />
                                          <div
                                            className="flex h-4 w-4 items-center justify-center rounded-full transition-all duration-200"
                                            style={{
                                              backgroundColor: isSelected
                                                ? policy.color
                                                : 'transparent',
                                              border: isSelected ? 'none' : '1.5px solid #CBD5E1',
                                              opacity: isSelected ? 1 : isHovered ? 0.6 : 1,
                                            }}
                                          >
                                            {isSelected && (
                                              <Check
                                                className="h-2.5 w-2.5 text-white"
                                                strokeWidth={3.5}
                                              />
                                            )}
                                          </div>
                                        </div>
                                        <div>
                                          <div
                                            className="text-sm font-semibold transition-colors duration-200"
                                            style={{ color: isSelected ? policy.color : '#020617' }}
                                          >
                                            {policy.label}
                                          </div>
                                          <div className="mt-0.5 text-xs leading-snug text-[#64748B]">
                                            {policy.description}
                                          </div>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </FormControl>
                        );
                      })()}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {children}
          </form>
        </Form>
      </div>
    );
  },
);

OrganizationForm.displayName = 'OrganizationForm';
